import {AddressElement, useElements, useStripe} from '@stripe/react-stripe-js'
import {
  type ContactOption,
  type StripeAddressElementChangeEvent,
  type StripeAddressElementOptions,
} from '@stripe/stripe-js'
import pRetry from 'p-retry'
import {Breadcrumb, Breadcrumbs, Form} from 'react-aria-components'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import useIsAuthenticated from '@/auth/hooks/useIsAuthenticated'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import useCartSummary from '@/lib/cart/hooks/useCartSummary'
import HeadTags from '@/lib/head/HeadTags'
import {isSupportedCountry, useCountry} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import type {ContactInfoControl} from '@/lib/payment/components/ContactInfo'
import ContactInfo from '@/lib/payment/components/ContactInfo'
import type {Contact} from '@/lib/payment/components/model'
import PaymentMethod from '@/lib/payment/components/PaymentMethod'
import {CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT} from '@/lib/payment/constants'
import {useAddresses} from '@/lib/payment/hooks/useAddresses'
import useCustomerSession from '@/lib/payment/hooks/useCustomerSession'
import {ShippingInfo, StripeAddress} from '@/lib/payment/paymenModel'
import PaymentProvider, {useSetElementsOptions} from '@/lib/payment/PaymentProvider'
import {createPaymentIntent} from '@/lib/payment/paymentService'
import {getRoundUp, useSetCustomItems} from '@/lib/payment/store'
import {RESOLVED_ERRORS, StripeElement} from '@/lib/payment/stripeErrors'
import toStripeAmountNumber from '@/lib/payment/utils/toStripeAmountNumber'
import {CheckoutResult} from '@/models/cartModel'
import {OrderStatus} from '@/models/orderModel'
import {CartRoute, CheckoutRoute, HomeRoute, PaymentRoute} from '@/routeRegistry'
import {buildStringForCalibratedError, isCartCalibratedError} from '@/services/cartService'
import {confirmOrder, getOrder} from '@/services/orderService'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import UnexpectedError from '@/utils/api/UnexpectedError'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import RACLink from '@/utils/router/RACLink'
import useSearch from '@/utils/router/useSearch'

import PaymentSummary from './components/PaymentSummary'
import style from './Payment.module.scss'

const Payment = memo(function Payment() {
  const router = useRouter()
  const store = useStore()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const latestLocale = useLatest(locale)
  const {custom} = PaymentRoute.useSearch()
  const [isRewardsApplied, setIsRewardsApplied] = useSearch(PaymentRoute.id, 'applyRewards')
  const [method, setMethod] = useSearch(PaymentRoute.id, 'method', {
    replace: false,
    resetScroll: true,
  })
  const [coupon, setCoupon] = useSearch(PaymentRoute.id, 'coupon')
  const latestMethod = useLatest(method)
  const latestCustom = useLatest(custom)

  const [loading, setLoading] = useState(false)
  const isAuthenticated = useIsAuthenticated()
  const latestIsAuthenticated = useLatest(isAuthenticated)
  const {data: customerSession} = useCustomerSession()
  const stripe = useStripe()
  const elements = useElements()
  const latestIsRewardsApplied = useLatest(isRewardsApplied)
  const account = useAccountInformationValue()
  const [country] = useCountry()
  const shippingInfoRef = useRef<ShippingInfo | undefined>(undefined)
  const {data: addresses, isPending: isCustomerPending} = useAddresses()
  const latestAddresses = useLatest(addresses)
  const contactInfoRef = useRef<ContactInfoControl>(null)
  const paymentMethodId = useId()

  const contacts = useMemo(() => {
    if (!addresses) {
      return []
    }

    if (addresses.shippingInfos) {
      return addresses.shippingInfos
        .map(shippingInfo => {
          const userCountry = shippingInfo.country ?? addresses.billingInfo?.country

          if (userCountry !== country) {
            return false
          }

          return {
            name: shippingInfo.name ?? addresses.billingInfo?.name ?? '',
            phone: shippingInfo.phone ?? addresses.billingInfo?.phone,
            address: {
              line1: shippingInfo.line1 ?? addresses.billingInfo?.line1 ?? '',
              line2: shippingInfo.line2 ?? addresses.billingInfo?.line2 ?? '',
              city: shippingInfo.city ?? addresses.billingInfo?.city ?? '',
              state: shippingInfo.state ?? addresses.billingInfo?.state ?? '',
              postal_code: shippingInfo.postalCode ?? addresses.billingInfo?.postalCode ?? '',
              country: userCountry,
            },
          } satisfies ContactOption
        })
        .filter(Boolean)
    }

    return []
  }, [addresses, country])

  console.log(contacts)

  const [address, setAddress] = useState<StripeAddress | undefined>()

  ;(function updateDefaultAddress() {
    if (address !== undefined) return

    const contact = contacts[0]

    if (!contact) return

    const newAddress = {...contact.address, postalCode: contact.address.postal_code}

    setAddress(newAddress)
  })()

  const addressOptions = useMemo<StripeAddressElementOptions>(() => {
    return {
      mode: 'shipping',
      fields: {
        phone: 'always',
      },
      validation: {
        phone: {
          required: 'always',
        },
      },
      // NOTE: known bug: when change country, the suggestions for address still be the old country
      allowedCountries: [country],
      defaultValues: {
        name: account?.accountName ?? '',
        address: {
          country,
        },
      },
      contacts,
    }
  }, [account?.accountName, contacts, country])

  console.log(addressOptions)

  const setElementsOptions = useSetElementsOptions()
  const setCustomItems = useSetCustomItems()

  const cartSummary = useCartSummary(isRewardsApplied, coupon, setCoupon, address, custom)
  const latestCoupon = useLatest(coupon)
  const latestItems = useLatest(cartSummary.cart?.originalItems)

  ;(function updateElementsOptions() {
    setElementsOptions(options => {
      options.mode = 'payment'
      options.setupFutureUsage = 'off_session' // vs on?
      if (customerSession) {
        options.customerSessionClientSecret = customerSession
      }
      options.amount = toStripeAmountNumber(cartSummary.total)
    })
  })()

  const handleSubmit = useCallback(
    async (contact: Contact, shouldShiftFocus = false) => {
      if (!stripe || !elements) {
        return
      }

      setLoading(true)

      try {
        const {error: submitError} = await elements.submit()
        if (submitError) {
          const resolveTo = RESOLVED_ERRORS.find(error =>
            [submitError.code, submitError.decline_code].includes(error.code),
          )

          if (resolveTo?.belongsTo === StripeElement.Address) {
            // move back to step 1
            void setMethod(false).then(() => {
              elements.getElement('address')?.focus()
            })
          } else if (resolveTo?.belongsTo === StripeElement.Payment) {
            // move to step 2
            void setMethod(true).then(() => {
              elements.getElement('payment')?.focus()
            })
          } else {
            // move back to step 1
            console.error('Stripe Error', submitError)
            toastErrorMessage('Unexpected error, please start over. Sorry for the inconvenience!')
            void setMethod(false).then(() => {
              elements.getElement('address')?.focus()
            })
          }

          return
        }

        if (!latestMethod.current) {
          void setMethod(true).then(() => {
            if (!shouldShiftFocus) return
            document.getElementById(paymentMethodId)?.focus()
          })
          return
        }

        const {error: confirmationError, confirmationToken} = await stripe.createConfirmationToken({
          elements,
          params: {
            payment_method_data: {
              allow_redisplay: 'always',
            },
          },
        })

        if (confirmationError) {
          console.error(confirmationError)
          toastErrorMessage(confirmationError)
          return
        }

        const returnUrl = router.buildLocation({
          to: CheckoutRoute.fullPath,
          params: {
            result: CheckoutResult.Success,
          },
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
          },
        }).href

        invariant(shippingInfoRef.current)

        // Create the PaymentIntent
        const {clientSecret, order, paymentIntentStatus} = await createPaymentIntent({
          isAuthenticated: latestIsAuthenticated.current,
          confirmationTokenId: confirmationToken.id,
          applyRewards: latestIsRewardsApplied.current,
          returnUrl,
          queryClient,
          locale: latestLocale.current,
          items: latestCustom.current ? latestItems.current : undefined,
          setCustomItems,
          email: contact.email,
          coupon: latestCoupon.current,
          addresses: latestAddresses.current,
          newAddress: shippingInfoRef.current,
          roundUp: getRoundUp(store),
        })

        if (clientSecret !== CLIENT_SECRET_FOR_0_AMOUNT_PAYMENT) {
          if (paymentIntentStatus === 'requires_action') {
            const {error: nextActionError} = await stripe.handleNextAction({clientSecret})

            if (nextActionError) {
              // eslint-disable-next-line @typescript-eslint/only-throw-error -- its okay
              throw nextActionError
            }

            await confirmOrder({
              orderId: order.id,
              locale: latestLocale.current,
            })
          }
        }

        const updatedOrder = await getOrder({
          orderId: order.id,
          locale: latestLocale.current,
        })

        if (updatedOrder === false) {
          throw new UnexpectedError()
        }

        if (updatedOrder.status === OrderStatus.AWAITING_PAYMENT) {
          await pRetry(
            () =>
              confirmOrder({
                orderId: order.id,
                locale: latestLocale.current,
              }),
            {retries: 3},
          )
        }

        await router.navigate({
          to: CheckoutRoute.fullPath,
          params: {
            result: CheckoutResult.Success,
          },
          search: {
            guest: !latestIsAuthenticated.current ? true : undefined,
            orderId: order.id,
          },
        })
      } catch (e) {
        if (isCartCalibratedError(e)) {
          toastErrorMessage(buildStringForCalibratedError(e))
        } else {
          console.error(e)
          toastErrorMessage(e)
        }
        return
      } finally {
        setLoading(false)
      }
    },
    [store, stripe, elements, router, queryClient, setCustomItems, paymentMethodId],
  )

  const handleAddressChange = useCallback((e: StripeAddressElementChangeEvent) => {
    if (!isSupportedCountry(e.value.address.country)) return
    shippingInfoRef.current = {
      line1: e.value.address.line1,
      line2: e.value.address.line2 ?? '',
      city: e.value.address.city,
      state: e.value.address.state,
      postalCode: e.value.address.postal_code,
      country: e.value.address.country,
      name: e.value.name,
      phone: e.value.phone,
    }
  }, [])

  const handleAddressBlur = useCallback(() => {
    setTimeout(() => {
      try {
        console.log(shippingInfoRef.current)
        const address = StripeAddress(shippingInfoRef.current)
        setAddress(address)
      } catch (e) {
        console.error(e)
        toastErrorMessage('Please enter a valid address')
      }
    }, 1000)
  }, [])

  const custommerInfoIsReadyOrNotCustomer = !customerSession || !isCustomerPending
  console.log(customerSession, isCustomerPending)

  const formRef = useRef<HTMLFormElement>(null)

  // ---------------------------------------------------------------------------

  const latestHandleSubmit = useStableCallback(handleSubmit)

  const onSubmitButtonPress = useCallback(async (shouldShiftFocus = false) => {
    const contact = await contactInfoRef.current?.submit()
    if (!contact) return
    void latestHandleSubmit(contact, shouldShiftFocus)
  }, [])

  // ---------------------------------------------------------------------------

  const title = method ? 'Payment method' : 'Checkout'

  return (
    <>
      <HeadTags title={title} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar
          transparentBg
          withoutLogoOnDesktop
          variant='light'
          className={style.Navbar}
          haveCart={false}
        />
        <main className={style.CartPage} {...skipTargetProps('main-content')}>
          <div className={style.CartContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={HomeRoute.fullPath}>
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={CartRoute.fullPath}
                    search={{
                      applyRewards: isRewardsApplied,
                      coupon,
                    }}
                  >
                    Cart
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={PaymentRoute.fullPath}
                    search={{method: false, applyRewards: isRewardsApplied, coupon, custom}}
                    activeOptions={{
                      exact: true,
                      includeSearch: true,
                    }}
                  >
                    Checkout
                  </RACLink>
                </Breadcrumb>
                {method && (
                  <Breadcrumb className={style.Breadcrumb}>
                    <RACLink
                      className={style.Link}
                      to={PaymentRoute.fullPath}
                      search={{method: true, applyRewards: isRewardsApplied, coupon, custom}}
                      activeOptions={{
                        exact: true,
                        includeSearch: true,
                      }}
                    >
                      Payment method
                    </RACLink>
                  </Breadcrumb>
                )}
              </Breadcrumbs>
            </div>
            <h1 className={style.Heading}>{title}</h1>
            <div className={style.Cart}>
              <div className={style.CartItems}>
                <VisuallyHidden
                  as='div'
                  isVisible={!method}
                  aria-hidden={method}
                  tabIndex={method ? -1 : undefined}
                  inert={method}
                  role={method ? 'none' : undefined}
                >
                  <>
                    <h2>Contact info</h2>
                    <ContactInfo ref={contactInfoRef} className={style.Wrapper} />
                  </>
                </VisuallyHidden>
                <Form
                  ref={formRef}
                  onSubmit={e => {
                    e.preventDefault()
                    void onSubmitButtonPress()
                  }}
                >
                  <VisuallyHidden
                    as='div'
                    isVisible={!method}
                    aria-hidden={method}
                    tabIndex={method ? -1 : undefined}
                    inert={method}
                    role={method ? 'none' : undefined}
                  >
                    <h2>Shipping address</h2>
                    <div className={style.Wrapper}>
                      {custommerInfoIsReadyOrNotCustomer && (
                        <AddressElement
                          onChange={handleAddressChange}
                          onBlur={handleAddressBlur}
                          options={addressOptions}
                        />
                      )}
                    </div>
                  </VisuallyHidden>
                  {method && (
                    <>
                      <h2 id={paymentMethodId} tabIndex={-1}>
                        Payment method
                      </h2>
                      <div className={style.Wrapper}>
                        <PaymentMethod total={cartSummary.total} />
                      </div>
                    </>
                  )}
                  <VisuallyHidden
                    as={PrimaryButton}
                    className={style.PayBtn2}
                    onPress={() => {
                      void onSubmitButtonPress(true)
                    }}
                    aria-label='Pay now (Guaranteed safe & secure, powered by Stripe)'
                  >
                    {(() => {
                      if (!method) return 'Next step'
                      return 'Pay now'
                    })()}
                  </VisuallyHidden>
                </Form>
              </div>
              {!isMobile && (
                <PaymentSummary
                  onSubmit={onSubmitButtonPress}
                  isPending={loading}
                  isRewardsApplied={isRewardsApplied}
                  setIsRewardsApplied={setIsRewardsApplied}
                  cartSummary={cartSummary}
                />
              )}
            </div>
          </div>
        </main>
        {isMobile && (
          <PaymentSummary
            onSubmit={onSubmitButtonPress}
            isPending={loading}
            isRewardsApplied={isRewardsApplied}
            setIsRewardsApplied={setIsRewardsApplied}
            cartSummary={cartSummary}
          />
        )}
        <Footer />
      </div>
    </>
  )
})

const PaymentWrapped = memo(function PaymentWrapped() {
  return (
    <PaymentProvider>
      <Payment />
    </PaymentProvider>
  )
})

export default PaymentWrapped
